import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { euroInt, stripParen, priceTtcOf, setDisplayCurrency } from "@/lib/format";
import { Loader2, ChevronRight, ArrowLeft, Package, MapPin } from "lucide-react";
import { motion } from "framer-motion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { PromoPicker } from "@/components/PromoPicker";
import { isPromoOn } from "@/lib/promoChoice";
import { useI18n } from "@/context/I18nContext";

const isIslandais = (s) => (s || "").toLowerCase().includes("island");

export default function ModelPicker() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const range = params.get("range");
  const group = params.get("group");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [countries, setCountries] = useState([]);
  const [countryCode, setCountryCode] = useState(() => localStorage.getItem("fautras_country") || "FR");
  const [promos, setPromos] = useState([]);
  const [promoTick, setPromoTick] = useState(0);
  const [sizeFilter, setSizeFilterState] = useState(() => {
    try { return JSON.parse(localStorage.getItem("fautras_size")) || { std: true, isl: false }; } catch { return { std: true, isl: false }; }
  });
  const setSizeFilter = (updater) => setSizeFilterState((s) => {
    const n = typeof updater === "function" ? updater(s) : updater;
    localStorage.setItem("fautras_size", JSON.stringify(n));
    return n;
  });
  const nav = useNavigate();

  useEffect(() => {
    if (!range) { setLoading(false); return; }
    api.get(`/menu/${range}`).then(({ data }) => setGroups(data.groups || [])).finally(() => setLoading(false));
  }, [range]);

  useEffect(() => {
    api.get("/countries").then(({ data }) => setCountries(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!range || !countryCode) { setPromos([]); return; }
    api.get(`/promotions/active`, { params: { country: countryCode, range_key: range } })
      .then(({ data }) => setPromos(data || [])).catch(() => setPromos([]));
  }, [range, countryCode]);

  const modelPubMap = useMemo(() => {
    const m = {};
    for (const p of promos.filter(isPromoOn)) for (const [id, r] of Object.entries(p.model_rules || {})) if ((r.pub || 0) > (m[id] || 0)) m[id] = r.pub || 0;
    return m;
  }, [promos, promoTick]); // eslint-disable-line react-hooks/exhaustive-deps
  const [loc, setLoc] = useState({ models: {}, pricing: null });
  useEffect(() => {
    if (range !== "van") { setLoc({ models: {}, pricing: null }); return; }
    api.get(`/catalog/${range}`, { params: { country: countryCode } })
      .then(({ data }) => setLoc({ models: Object.fromEntries((data.models || []).map((m) => [m.id, m])), pricing: data.pricing || null }))
      .catch(() => setLoc({ models: {}, pricing: null }));
  }, [range, countryCode]);
  setDisplayCurrency(loc.pricing?.currency, loc.pricing?.rate);
  const priceTtc = (model) => priceTtcOf(loc.models[model.id] || model, vat) * (1 - (modelPubMap[model.id] || 0) / 100);

  const vat = useMemo(() => {
    const c = countries.find((x) => x.code === countryCode);
    return c ? c.vat_rate : 0.2;
  }, [countries, countryCode]);

  const groupDef = useMemo(() => groups.find((g) => g.key === group), [groups, group]);
  const entries = groupDef?.entries || [];
  const showSizeFilter = range === "van";
  const shownEntries = showSizeFilter
    ? entries.filter((e) => (isIslandais(e.size) ? sizeFilter.isl : sizeFilter.std))
    : entries;
  const go = (m) => nav(`/configurer?range=${range}&group=${group}&model=${m.id}`);
  const goFamily = (e) => nav(`/versions?range=${range}&group=${group}&family=${encodeURIComponent(e.label)}`);

  if (loading) {
    return <div className="flex items-center justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
      <PromoPicker promos={promos} onChange={() => setPromoTick((x) => x + 1)} testid="picker-promo" />
      <Link to="/selection" data-testid="picker-back" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 mb-2">
        <ArrowLeft className="w-4 h-4" /> {t("Retour aux schémas")}
      </Link>

      <div className="mb-4">
        <div className="text-[10px] font-bold uppercase tracking-widest text-primary">{t(range === "van" ? "Van chevaux" : "Remorques fourgon Pro & Sport")}</div>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mt-0.5">
          {groupDef ? t(groupDef.label.replace(/diagonal\b/gi, "diagonale")) : t("Choisissez votre modèle")}
        </h1>

        <div className="mt-2.5 flex flex-wrap items-center gap-x-6 gap-y-2">
        <div className="flex items-center gap-2" data-testid="picker-country-row">
          <MapPin className="w-4 h-4 text-primary" />
          <span className="text-sm text-muted-foreground">{t("Pays de livraison")} :</span>
          <Select value={countryCode} onValueChange={(v) => { setCountryCode(v); localStorage.setItem("fautras_country", v); }}>
            <SelectTrigger className="w-56 h-9" data-testid="picker-country-select">
              <SelectValue placeholder={t("Pays")} />
            </SelectTrigger>
            <SelectContent>
              {countries.map((c) => (
                <SelectItem key={c.code} value={c.code} data-testid={`picker-country-${c.code}`}>
                  {t(c.name)} · {t("TVA")} {Math.round(c.vat_rate * 1000) / 10}%
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {showSizeFilter && (
          <div className="flex flex-wrap items-end gap-8" data-testid="picker-size-filter">
            <label className="flex items-end gap-2 text-sm cursor-pointer" data-testid="size-std-label">
              <Checkbox checked={sizeFilter.std} onCheckedChange={(v) => setSizeFilter((s) => ({ ...s, std: !!v }))} data-testid="size-std-checkbox" />
              <img src="/assets/cheval-standard.png" alt="" className="max-h-9 w-auto" />
              {t("Chevaux standard")}
            </label>
            <label className="flex items-end gap-2 text-sm cursor-pointer" data-testid="size-isl-label">
              <Checkbox checked={sizeFilter.isl} onCheckedChange={(v) => setSizeFilter((s) => ({ ...s, isl: !!v }))} data-testid="size-isl-checkbox" />
              <img src="/assets/cheval-islandais.png" alt="" className="max-h-6 w-auto" />
              {t("Chevaux islandais")}
            </label>
          </div>
        )}
        </div>
      </div>

      {shownEntries.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-border rounded-xl text-muted-foreground">
          {t("Aucun modèle disponible dans cette catégorie.")}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {shownEntries.map((e, i) => {
            const familyImg = (versions = []) => {
              const withImg = versions.filter((v) => v.model?.image);
              const classic = withImg.find((v) => /classic/i.test(v.label) || /classic/i.test(v.model?.name || ""));
              return (classic || withImg[0] || versions[0])?.model?.image || "";
            };
            const img = e.type === "leaf" ? e.model.image : familyImg(e.versions);
            return (
              <motion.div
                key={e.label}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.04 }}
                className="rounded-2xl border border-border bg-card overflow-hidden transition-all duration-300 hover:border-primary/60 hover:-translate-y-1 hover:shadow-xl hover:shadow-primary/10"
              >
                <button
                  data-testid={`entry-${e.label}`}
                  onClick={() => (e.type === "leaf" ? go(e.model) : goFamily(e))}
                  className="w-full text-left group block"
                >
                  <div className="aspect-[4/3] bg-white flex items-center justify-center overflow-hidden">
                    {img
                      ? <img src={img} alt={e.label} className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.06]" />
                      : <Package className="w-12 h-12 text-slate-300" strokeWidth={1.25} />}
                  </div>
                  <div className="px-4 py-3 flex items-center justify-between gap-2 border-t border-border/60">
                    <div className="min-w-0">
                      <div className="mb-1 h-[18px]">
                        {e.type === "family" && (
                          <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
                            {e.versions.length} {t("versions")}
                          </span>
                        )}
                      </div>
                      <div className="font-display text-lg font-bold leading-tight line-clamp-2 min-h-[2.5em] uppercase">{t(range === "van" ? stripParen(e.label) : e.label)}</div>
                      {e.type === "leaf"
                        ? <div className="text-base font-mono text-[#9E7B41] font-semibold mt-1 h-6 flex items-center gap-1">{euroInt(priceTtc(e.model))} <span className="text-xs text-muted-foreground font-sans">{t("TTC")}</span></div>
                        : <div className="text-sm text-muted-foreground mt-1 h-6 flex items-center gap-1">{t("À partir de")} <span className="font-mono text-[#9E7B41] font-semibold">{euroInt(Math.min(...e.versions.map((v) => priceTtc(v.model))))}</span> {t("TTC")}</div>}
                      {e.type === "leaf" && e.meta && <div className="text-xs text-muted-foreground mt-1">{e.meta}</div>}
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all shrink-0" />
                  </div>
                </button>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
